import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HitsterEvent, EventTeam, EventRound } from '../types';
import { Button } from '../components/shared/Button';
import './Scoreboard.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface TeamStanding {
  team: EventTeam;
  totalScore: number;
  roundScores: { roundId: string; roundName: string; score: number }[];
}

const Scoreboard = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (!response.ok) throw new Error('Event not found');
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        console.error('Error fetching event:', err);
      }
      setLoading(false);
    };

    if (eventId) {
      fetchEvent();
      // Poll every 10 seconds so scores entered on another device are picked up automatically
      const interval = setInterval(fetchEvent, 10000);
      return () => clearInterval(interval);
    }
  }, [eventId]);

  useEffect(() => {
    if (!event) return;

    const teams = event.teams || [];
    const rounds = event.rounds || [];

    const teamStandings: TeamStanding[] = teams.map(team => {
      const roundScores = rounds.map(round => {
        const roundData = team.roundData?.find(rd => rd.roundId === round.id);
        return {
          roundId: round.id,
          roundName: round.name,
          score: roundData?.totalScore || 0,
        };
      });

      const totalScore = roundScores.reduce((sum, rs) => sum + rs.score, 0);

      return {
        team,
        totalScore,
        roundScores,
      };
    });

    // Sort by total score descending
    teamStandings.sort((a, b) => b.totalScore - a.totalScore);

    setStandings(teamStandings);
  }, [event]);

  if (loading) {
    return (
      <div className="scoreboard loading">
        <p>Laden...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="scoreboard error">
        <p>Evenement niet gevonden</p>
        <Button onClick={() => navigate('/events')}>Terug naar Events</Button>
      </div>
    );
  }

  const rounds = event.rounds || [];
  const maxPossiblePerRound = 10 * 4; // 10 songs × 4 points

  return (
    <div className="scoreboard">
      <div className="scoreboard-header">
        <div className="header-left">
          <Button variant="secondary" onClick={() => navigate(`/events`)}>
            ← Terug
          </Button>
          <div className="header-info">
            <h1>🏆 Scorebord</h1>
            <h2>{event.name}</h2>
          </div>
        </div>
      </div>

      <div className="scoreboard-content">
        {/* Total Standings */}
        <div className="standings-section">
          <h3>Totaal Stand</h3>
          <div className="standings-list">
            {standings.map((standing, index) => (
              <div 
                key={standing.team.id} 
                className={`standing-row ${index === 0 ? 'first' : index === 1 ? 'second' : index === 2 ? 'third' : ''}`}
              >
                <div className="position">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                </div>
                <div className="team-name">{standing.team.name}</div>
                <div className="total-score">{standing.totalScore} pt</div>
              </div>
            ))}
          </div>
        </div>

        {/* Per Round Breakdown */}
        <div className="rounds-section">
          <h3>Score per Ronde</h3>
          <div className="rounds-table-wrapper">
            <table className="rounds-table">
              <thead>
                <tr>
                  <th className="team-col">Team</th>
                  {rounds.map(round => (
                    <th key={round.id} className="round-col">{round.name}</th>
                  ))}
                  <th className="total-col">Totaal</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((standing, index) => (
                  <tr key={standing.team.id} className={index < 3 ? `top-${index + 1}` : ''}>
                    <td className="team-col">
                      <span className="position-badge">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`}
                      </span>
                      {standing.team.name}
                    </td>
                    {standing.roundScores.map(rs => (
                      <td key={rs.roundId} className="round-col">
                        <span className="score">{rs.score}</span>
                        <span className="max">/ {maxPossiblePerRound}</span>
                      </td>
                    ))}
                    <td className="total-col">
                      <strong>{standing.totalScore}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Stats */}
        {standings.length > 0 && (
          <div className="stats-section">
            <h3>Statistieken</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Hoogste score</div>
                <div className="stat-value">{standings[0]?.totalScore || 0} pt</div>
                <div className="stat-team">{standings[0]?.team.name}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Gemiddelde score</div>
                <div className="stat-value">
                  {standings.length > 0 
                    ? Math.round(standings.reduce((sum, s) => sum + s.totalScore, 0) / standings.length)
                    : 0} pt
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Rondes gespeeld</div>
                <div className="stat-value">{rounds.length}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Teams</div>
                <div className="stat-value">{standings.length}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Scoreboard;
